import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { useEffect, Suspense, lazy, useState } from "react";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import CanonicalTag from "@/components/CanonicalTag";
import { TrailingSlashRedirect } from "@/components/TrailingSlashRedirect";
import { HreflangTags } from "@/components/HreflangTags";
import { PageLoader } from "@/components/PageLoader";

// Global UI components - lazy loaded to reduce initial bundle
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
import { TooltipProvider } from "@/components/ui/tooltip";
const FloatingChat = lazy(() => import("@/components/FloatingChat"));
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));
const BookNowButton = lazy(() => import("@/components/BookNowButton"));

import { DelayedMount } from "@/components/DelayedMount";

// Critical pages - Index loaded immediately, CaravanDetails lazy for smaller initial bundle
import Index from "./pages/Index";
const CaravanDetails = lazy(() => import("./pages/CaravanDetails"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Pages
const Login = lazy(() => import("./pages/Login"));
const Cart = lazy(() => import("./pages/Cart"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const PaymentCallback = lazy(() => import("./pages/PaymentCallback"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

// Public pages - lazy loaded for performance
const FAQ = lazy(() => import("./pages/FAQ"));
const OurServices = lazy(() => import("./pages/OurServices"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const GetQuote = lazy(() => import("./pages/GetQuote"));
const PartnerProgram = lazy(() => import("./pages/PartnerProgram"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const MotorhomeLanding = lazy(() => import("./pages/MotorhomeLanding"));
const Campsites = lazy(() => import("./pages/Campsites"));
const CompletePayment = lazy(() => import("./pages/CompletePayment"));

// Admin pages
const AdminLayout = lazy(() => import("./pages/admin/Layout"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminCalendar = lazy(() => import("./pages/admin/Calendar"));
const AdminBookings = lazy(() => import("./pages/admin/Bookings"));
const AdminCaravans = lazy(() => import("./pages/admin/Caravans"));
const AdminSales = lazy(() => import("./pages/admin/Sales"));
const AdminInbox = lazy(() => import("./pages/admin/Inbox"));
const AdminBlog = lazy(() => import("./pages/admin/BlogNew"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminMarketing = lazy(() => import("./pages/admin/Marketing"));
const AdminProfile = lazy(() => import("./pages/admin/Profile"));
const AdminSEO = lazy(() => import("./pages/admin/SEO"));
const AdminMultilingual = lazy(() => import("./pages/admin/Multilingual"));
const AdminTranslationManager = lazy(() => import("./pages/admin/TranslationManager"));
const AdminTextScanner = lazy(() => import("./pages/admin/TextScanner"));
const AdminLanguageManager = lazy(() => import("./pages/admin/LanguageManager"));
const AdminCaravanTranslations = lazy(() => import("./pages/admin/CaravanTranslations"));
const OwnersManagement = lazy(() => import("./pages/admin/OwnersManagement"));
const AdminCaravanModeration = lazy(() => import("./pages/admin/CaravanModeration"));
const AdminContractManagement = lazy(() => import("./pages/admin/ContractManagement"));
const AdminQuoteRequests = lazy(() => import("./pages/admin/QuoteRequests"));
const AdminDemoUsers = lazy(() => import("./pages/admin/DemoUsers"));
const AdminReviewsModeration = lazy(() => import("./pages/admin/ReviewsModeration"));
const AdminCampsites = lazy(() => import("./pages/admin/CampsitesManagement"));

// Owner pages
const OwnerRegister = lazy(() => import("./pages/owner/OwnerRegister"));
const OwnerContract = lazy(() => import("./pages/owner/Contract"));
const OwnerPending = lazy(() => import("./pages/owner/Pending"));
const OwnerLayout = lazy(() => import("./pages/owner/Layout"));
const OwnerDashboard = lazy(() => import("./pages/owner/Dashboard"));
const OwnerCaravans = lazy(() => import("./pages/owner/Caravans"));
const OwnerCaravanNew = lazy(() => import("./pages/owner/CaravanNew"));
const OwnerCaravanEdit = lazy(() => import("./pages/owner/CaravanEdit"));
const OwnerCalendar = lazy(() => import("./pages/owner/Calendar"));
const OwnerBookings = lazy(() => import("./pages/owner/Bookings"));
const OwnerProfile = lazy(() => import("./pages/owner/Profile"));
const OwnerLogin = lazy(() => import("./pages/owner/Login"));

// Demo pages
const DemoRegister = lazy(() => import("./pages/demo/DemoRegister"));
const DemoLayout = lazy(() => import("./pages/demo/DemoLayout"));
const DemoDashboard = lazy(() => import("./pages/demo/DemoDashboard"));
const DemoCaravans = lazy(() => import("./pages/demo/DemoCaravans"));
const DemoCaravanNew = lazy(() => import("./pages/demo/DemoCaravanNew"));
const DemoCaravanEdit = lazy(() => import("./pages/demo/DemoCaravanEdit"));
const DemoBookings = lazy(() => import("./pages/demo/DemoBookings"));
const DemoCalendar = lazy(() => import("./pages/demo/DemoCalendar"));
const DemoProfile = lazy(() => import("./pages/demo/DemoProfile"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Use shared language codes loader from LanguageContext (prevents duplicate queries)
import { loadValidLanguages as loadLanguageCodes } from '@/contexts/LanguageContext';

// Language route wrapper component
const LanguageRouteWrapper = ({ children }: { children: React.ReactNode }) => {
  const { lang } = useParams<{ lang?: string }>();
  const [validLanguages, setValidLanguages] = useState<string[]>(['de', 'tr', 'ru', 'es', 'it', 'nl']);

  useEffect(() => {
    loadLanguageCodes().then(setValidLanguages);
  }, []);

  // Only render children if lang is a valid language code
  if (lang && !validLanguages.includes(lang)) {
    return <Navigate to={window.location.pathname.replace(`/${lang}`, '') || '/'} replace />;
  }

  return <>{children}</>;
};

// Legacy URL redirect component for old caravan URLs
const LegacyCaravanRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/caravan/${slug}`} replace />;
};

// Legacy URL redirect to homepage (for URLs that don't exist anymore)
const LegacyToHomeRedirect = () => {
  return <Navigate to="/" replace />;
};

// Legacy URL redirect for language-prefixed old URLs
const LangLegacyRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  const validLanguages = ['de', 'tr', 'ru', 'es', 'it', 'nl'];
  if (lang && validLanguages.includes(lang)) {
    return <Navigate to={`/${lang}`} replace />;
  }
  return <Navigate to="/" replace />;
};

// Legacy contact page redirect
const LegacyContactRedirect = () => {
  return <Navigate to="/contact" replace />;
};

// Legacy contact page redirect with language
const LangLegacyContactRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  const validLanguages = ['de', 'tr', 'ru', 'es', 'it', 'nl'];
  if (lang && validLanguages.includes(lang)) {
    return <Navigate to={`/${lang}/contact`} replace />;
  }
  return <Navigate to="/contact" replace />;
};

// Legacy about page redirect with language
const LangLegacyAboutRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  const validLanguages = ['de', 'tr', 'ru', 'es', 'it', 'nl'];
  if (lang && validLanguages.includes(lang)) {
    return <Navigate to={`/${lang}/about`} replace />;
  }
  return <Navigate to="/about" replace />;
};

// Legacy camping redirect with language
const LangCampingRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  const validLanguages = ['de', 'tr', 'ru', 'es', 'it', 'nl'];
  if (lang && validLanguages.includes(lang)) {
    return <Navigate to={`/${lang}/camping-places-turkey`} replace />;
  }
  return <Navigate to="/camping-places-turkey" replace />;
};

// Legacy quote page redirect with language
const LangLegacyQuoteRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  const validLanguages = ['de', 'tr', 'ru', 'es', 'it', 'nl'];
  if (lang && validLanguages.includes(lang)) {
    return <Navigate to={`/${lang}/get-quote`} replace />;
  }
  return <Navigate to="/get-quote" replace />;
};

// Legacy services page redirect with language
const LangLegacyServicesRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  const validLanguages = ['de', 'tr', 'ru', 'es', 'it', 'nl'];
  if (lang && validLanguages.includes(lang)) {
    return <Navigate to={`/${lang}/services`} replace />;
  }
  return <Navigate to="/services" replace />;
};

// Custom Hook for Interaction Detection
const useInteraction = () => {
  const [isInteracted, setIsInteracted] = useState(false);

  useEffect(() => {
    const handleInteraction = () => {
      setIsInteracted(true);
    };

    const events = ['scroll', 'touchstart', 'mousemove', 'click', 'keydown'];
    
    // Add listeners
    events.forEach(event => {
      window.addEventListener(event, handleInteraction, { once: true, passive: true });
    });

    // Fallback timer
    const timer = setTimeout(() => {
      setIsInteracted(true);
    }, 5000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleInteraction);
      });
      clearTimeout(timer);
    };
  }, []);

  return isInteracted;
};

function AppContent() {
  const location = useLocation();
  const isInteracted = useInteraction();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  return (
    <>
      {/* Keep canonical/hreflang OUTSIDE Suspense so they always apply immediately */}
      <TrailingSlashRedirect />
      <CanonicalTag />
      {/* HreflangTags triggers language fetch - only render on interaction to save initial main thread */}
      {isInteracted && <HreflangTags />}

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes without language prefix (default English) */}
          <Route path="/" element={<Index />} />
          <Route path="/caravan/:slug" element={<CaravanDetails />} />
          {/* Legacy URL redirects - redirect old formats to homepage or correct pages */}
          <Route path="/check-a-details/:slug" element={<LegacyToHomeRedirect />} />
          <Route path="/caravans/:slug" element={<LegacyToHomeRedirect />} />
          <Route path="/copy-of-caravans/:slug" element={<LegacyToHomeRedirect />} />
          <Route path="/contact-us-turkey-camper" element={<LegacyContactRedirect />} />
          <Route path="/about-us-turkey-camper" element={<Navigate to="/about" replace />} />
          <Route path="/reserve" element={<Navigate to="/get-quote" replace />} />
          <Route path="/get-a-quote" element={<Navigate to="/get-quote" replace />} />
          <Route path="/would-you-like-to-work-with-us" element={<Navigate to="/partner-program" replace />} />
          <Route path="/blank-16" element={<LegacyToHomeRedirect />} />
          <Route path="/our-services" element={<Navigate to="/services" replace />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/payment-callback" element={<PaymentCallback />} />
          <Route path="/payment-process" element={<PaymentCallback />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<UserDashboard />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/post/:slug" element={<BlogPost />} />
          <Route path="/services" element={<OurServices />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/get-quote" element={<GetQuote />} />
          <Route path="/partner-program" element={<PartnerProgram />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/motorhome-hire-turkey" element={<MotorhomeLanding />} />
          <Route path="/campervan-rental-istanbul" element={<Index defaultCity="Istanbul" />} />
          <Route path="/motorhome-hire-antalya" element={<Index defaultCity="Antalya" />} />
          <Route path="/campervan-rental-ankara" element={<Index defaultCity="Ankara" />} />
          <Route path="/motorhome-hire-fethiye" element={<Index defaultCity="Fethiye" />} />
          <Route path="/campervan-rental-izmir" element={<Index defaultCity="Izmir" />} />
          <Route path="/campervan-rental-bodrum" element={<Index defaultCity="Bodrum" />} />
          <Route path="/camping-places-turkey" element={<Campsites />} />
          <Route path="/all-camping-places-turkey" element={<Navigate to="/camping-places-turkey" replace />} />
          <Route path="/complete-payment" element={<CompletePayment />} />

          {/* Public routes WITH language prefix (/:lang/*) */}
          <Route path="/:lang" element={<LanguageRouteWrapper><Index /></LanguageRouteWrapper>} />
          <Route path="/:lang/caravan/:slug" element={<LanguageRouteWrapper><CaravanDetails /></LanguageRouteWrapper>} />
          <Route path="/:lang/cart" element={<LanguageRouteWrapper><Cart /></LanguageRouteWrapper>} />
          <Route path="/:lang/payment-callback" element={<LanguageRouteWrapper><PaymentCallback /></LanguageRouteWrapper>} />
          <Route path="/:lang/login" element={<LanguageRouteWrapper><Login /></LanguageRouteWrapper>} />
          <Route path="/:lang/dashboard" element={<LanguageRouteWrapper><UserDashboard /></LanguageRouteWrapper>} />
          <Route path="/:lang/thank-you" element={<LanguageRouteWrapper><ThankYou /></LanguageRouteWrapper>} />
          <Route path="/:lang/faq" element={<LanguageRouteWrapper><FAQ /></LanguageRouteWrapper>} />
          <Route path="/:lang/blog" element={<LanguageRouteWrapper><Blog /></LanguageRouteWrapper>} />
          <Route path="/:lang/post/:slug" element={<LanguageRouteWrapper><BlogPost /></LanguageRouteWrapper>} />
          <Route path="/:lang/services" element={<LanguageRouteWrapper><OurServices /></LanguageRouteWrapper>} />
          <Route path="/:lang/about" element={<LanguageRouteWrapper><AboutUs /></LanguageRouteWrapper>} />
          <Route path="/:lang/contact" element={<LanguageRouteWrapper><ContactUs /></LanguageRouteWrapper>} />
          <Route path="/:lang/get-quote" element={<LanguageRouteWrapper><GetQuote /></LanguageRouteWrapper>} />
          <Route path="/:lang/partner-program" element={<LanguageRouteWrapper><PartnerProgram /></LanguageRouteWrapper>} />
          <Route path="/:lang/cookie-policy" element={<LanguageRouteWrapper><CookiePolicy /></LanguageRouteWrapper>} />
          <Route path="/:lang/privacy-policy" element={<LanguageRouteWrapper><PrivacyPolicy /></LanguageRouteWrapper>} />
          <Route path="/:lang/terms-and-conditions" element={<LanguageRouteWrapper><TermsAndConditions /></LanguageRouteWrapper>} />
          <Route path="/:lang/motorhome-hire-turkey" element={<LanguageRouteWrapper><MotorhomeLanding /></LanguageRouteWrapper>} />
          <Route path="/:lang/campervan-rental-istanbul" element={<LanguageRouteWrapper><Index defaultCity="Istanbul" /></LanguageRouteWrapper>} />
          <Route path="/:lang/motorhome-hire-antalya" element={<LanguageRouteWrapper><Index defaultCity="Antalya" /></LanguageRouteWrapper>} />
          <Route path="/:lang/campervan-rental-ankara" element={<LanguageRouteWrapper><Index defaultCity="Ankara" /></LanguageRouteWrapper>} />
          <Route path="/:lang/motorhome-hire-fethiye" element={<LanguageRouteWrapper><Index defaultCity="Fethiye" /></LanguageRouteWrapper>} />
          <Route path="/:lang/campervan-rental-izmir" element={<LanguageRouteWrapper><Index defaultCity="Izmir" /></LanguageRouteWrapper>} />
          <Route path="/:lang/campervan-rental-bodrum" element={<LanguageRouteWrapper><Index defaultCity="Bodrum" /></LanguageRouteWrapper>} />
          <Route path="/:lang/camping-places-turkey" element={<LanguageRouteWrapper><Campsites /></LanguageRouteWrapper>} />
          <Route path="/:lang/all-camping-places-turkey" element={<LangCampingRedirect />} />

          {/* Legacy URL redirects for language-prefixed URLs */}
          <Route path="/:lang/check-a-details/:slug" element={<LangLegacyRedirect />} />
          <Route path="/:lang/caravans/:slug" element={<LangLegacyRedirect />} />
          <Route path="/:lang/copy-of-caravans/:slug" element={<LangLegacyRedirect />} />
          <Route path="/:lang/contact-us-turkey-camper" element={<LangLegacyContactRedirect />} />
          <Route path="/:lang/about-us-turkey-camper" element={<LangLegacyAboutRedirect />} />
          <Route path="/:lang/get-a-quote" element={<LangLegacyQuoteRedirect />} />
          <Route path="/:lang/our-services" element={<LangLegacyServicesRedirect />} />

          {/* Demo Routes - Public sandbox */}
          <Route path="/demo/register" element={<DemoRegister />} />
          <Route path="/demo/dashboard" element={<DemoLayout />}>
            <Route index element={<DemoDashboard />} />
            <Route path="caravans" element={<DemoCaravans />} />
            <Route path="caravans/new" element={<DemoCaravanNew />} />
            <Route path="caravans/edit/:id" element={<DemoCaravanEdit />} />
            <Route path="bookings" element={<DemoBookings />} />
            <Route path="calendar" element={<DemoCalendar />} />
            <Route path="profile" element={<DemoProfile />} />
          </Route>
          <Route path="/:lang/demo/register" element={<LanguageRouteWrapper><DemoRegister /></LanguageRouteWrapper>} />

          {/* Owner Routes */}
          <Route path="/owner/login" element={<OwnerLogin />} />
          <Route path="/owner/register" element={<OwnerRegister />} />
          <Route path="/owner/contract" element={<OwnerContract />} />
          <Route path="/owner/pending" element={<OwnerPending />} />
          <Route path="/owner/dashboard" element={<OwnerLayout />}>
            <Route index element={<OwnerDashboard />} />
            <Route path="caravans" element={<OwnerCaravans />} />
            <Route path="caravans/new" element={<OwnerCaravanNew />} />
            <Route path="caravans/edit/:id" element={<OwnerCaravanEdit />} />
            <Route path="calendar" element={<OwnerCalendar />} />
            <Route path="bookings" element={<OwnerBookings />} />
            <Route path="profile" element={<OwnerProfile />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="caravans" element={<AdminCaravans />} />
            <Route path="sales" element={<AdminSales />} />
            <Route path="inbox" element={<AdminInbox />} />
            <Route path="quote-requests" element={<AdminQuoteRequests />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="marketing" element={<AdminMarketing />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="owners" element={<OwnersManagement />} />
            <Route path="caravan-moderation" element={<AdminCaravanModeration />} />
            <Route path="contract-management" element={<AdminContractManagement />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="multilingual" element={<AdminMultilingual />} />
            <Route path="multilingual/language-manager" element={<AdminLanguageManager />} />
            <Route path="multilingual/translation-manager" element={<AdminTranslationManager />} />
            <Route path="multilingual/text-scanner" element={<AdminTextScanner />} />
            <Route path="multilingual/caravan-translations" element={<AdminCaravanTranslations />} />
            <Route path="demo-users" element={<AdminDemoUsers />} />
            <Route path="reviews" element={<AdminReviewsModeration />} />
            <Route path="campsites" element={<AdminCampsites />} />
          </Route>
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      {/* Global UI Components - Rendered only after interaction to prevent TBT */}
      {isInteracted && (
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
          <FloatingChat />
          <WhatsAppButton />
          <BookNowButton />
        </Suspense>
      )}
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <CurrencyProvider>
            <CartProvider>
              <TooltipProvider>
                <AppContent />
              </TooltipProvider>
            </CartProvider>
          </CurrencyProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
